import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Helmet } from 'react-helmet-async';
import { Copy, Gift, Users, Link, Check, Loader, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const InvitationPage = () => {
    const { user, token } = useAuth();
    const [referrals, setReferrals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [copySuccess, setCopySuccess] = useState('');

    const invitationLink = user ? `${window.location.origin}/signup?ref=${user.invitationCode}` : '';

    useEffect(() => {
        const fetchReferralData = async () => {
            if (!token) {
                setLoading(false);
                setError("Authentication required to view referrals.");
                return;
            }
            try {
                const res = await axios.get('/api/profile/referrals', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setReferrals(res.data);
            } catch (err) {
                setError('Failed to load referral information.');
            } finally {
                setLoading(false);
            }
        };

        fetchReferralData();
    }, [token]);

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        setCopySuccess(text);
        setTimeout(() => setCopySuccess(''), 2500);
    };
    
    if (!user) {
        return (
             <div className="w-full min-h-screen pb-20 from-gray-900 via-blue-900/30 to-gray-900 text-white font-sans p-4 flex justify-center items-center">
                <Loader className="animate-spin" />
            </div>
        )
    }

    return (
        <>
        <Helmet>
            <title>Invite Friends & Earn | RatingApp</title>
            <meta name="description" content="Invite friends to RatingApp with your unique referral link and earn bonus rewards when they join and start rating apps." />
        </Helmet>
        <div className="w-full min-h-screen  from-gray-900 via-blue-900/30 to-gray-900 text-white font-sans p-4">
            <div className="max-w-2xl mx-auto">
                <motion.div 
                    className="text-center mb-12"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500 mb-3">
                        Invite & Earn
                    </h1>
                    <p className="text-slate-400 max-w-xl mx-auto">Share your unique link with friends. You'll earn 10 USDT for every friend who signs up and completes their first task.</p>
                </motion.div>

                <motion.div 
                    className="grid md:grid-cols-2 gap-6 mb-12"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                >
                    <InfoCard title="Your Invitation Code" value={user.invitationCode} onCopy={() => handleCopy(user.invitationCode)} copySuccess={copySuccess === user.invitationCode} />
                    <InfoCard title="Your Invitation Link" value={invitationLink} onCopy={() => handleCopy(invitationLink)} copySuccess={copySuccess === invitationLink} icon={<Link size={20} />} />
                </motion.div>

                {/* Total Invite Earnings Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="mb-8"
                >
                  <div className="relative bg-gradient-to-br from-green-400/80 via-cyan-400/80 to-blue-500/80 border-2 border-green-300/40 shadow-lg rounded-2xl p-6 flex flex-col items-center justify-center text-center max-w-xs mx-auto overflow-hidden">
                    <div className="absolute -top-8 -right-8 opacity-20 text-white pointer-events-none select-none">
                      <Gift size={120} />
                    </div>
                    <div className="flex flex-col items-center z-10">
                      <span className="inline-block bg-white/20 text-green-900 font-bold text-xs px-3 py-1 rounded-full mb-2 tracking-wide shadow">Total Invite Earnings</span>
                      <span className="text-4xl font-extrabold text-white drop-shadow-lg mb-1">{(referrals.length * 10).toFixed(2)} <span className="text-green-200 text-2xl font-bold">USDT</span></span>
                      <span className="text-green-50 text-xs font-medium">Earn 10 USDT for every successful referral</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                >
                    <h2 className="text-2xl font-bold mb-4 flex items-center gap-3"><Users /> Your Referrals ({referrals.length})</h2>
                    <div className="bg-gray-800/20 backdrop-blur-lg border border-white/10 rounded-2xl shadow-lg min-h-[200px] p-2">
                        {loading ? (
                             <div className="flex justify-center items-center gap-2 py-20 text-slate-400">
                                <Loader className="animate-spin" /> <span>Loading referrals...</span>
                            </div>
                        ) : error ? (
                             <div className="flex flex-col justify-center items-center gap-2 py-20 text-red-400">
                                <AlertTriangle /> <span>{error}</span>
                            </div>
                        ) : referrals.length === 0 ? (
                            <div className="text-center py-12 text-slate-500 flex flex-col items-center gap-4">
                                <Gift size={40} />
                                <h3 className="font-bold text-lg">No Referrals Yet</h3>
                                <p className="text-sm">Share your link to start earning rewards!</p>
                            </div>
                        ) : (
                            <ul className="divide-y divide-white/10">
                                {referrals.map(ref => (
                                    <li key={ref._id} className="py-3 px-4 flex justify-between items-center">
                                        <span className="font-medium text-slate-200">{ref.username}</span>
                                        <span className="text-sm text-slate-400">{new Date(ref.createdAt).toLocaleDateString()}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </motion.div>
            </div>
        </div>
        </>
    );
};

const InfoCard = ({ title, value, onCopy, copySuccess, icon }) => (
    <div className="bg-gray-800/30 backdrop-blur-lg border border-white/10 rounded-2xl p-5 flex flex-col justify-between">
        <h3 className="text-slate-400 text-sm font-medium mb-2">{title}</h3>
        <p className="text-lg font-mono text-cyan-300 break-words mb-4 flex-grow">{value}</p>
        <motion.button 
            onClick={onCopy} 
            className={`w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm font-semibold transition-colors ${copySuccess ? 'bg-green-500/80 text-white' : 'bg-cyan-500/80 hover:bg-cyan-500 text-white'}`}
            whileTap={{ scale: 0.95 }}
        >
            {copySuccess ? <><Check size={18} /> Copied!</> : <>{icon || <Copy size={18} />} Copy</>}
        </motion.button>
    </div>
);

export default InvitationPage; 